import { useState, useEffect, useMemo, useCallback } from "react";
import { apiGet } from "../api";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from "recharts";

const SUPPLIER_COLORS = [
  "#60F0F8", "#F050F8", "#18F0B8", "#FFD84F", "#FF6B9D",
  "#5840FF", "#FF9F43", "#A78BFA", "#34D399", "#FB7185",
  "#38BDF8", "#FBBF24", "#C084FC", "#F97316", "#22D3EE",
];

/* Extracted constants to avoid inline object re-creation on every render */
const CHART_MARGIN = { top: 10, right: 20, left: 10, bottom: 5 };
const GRID_STROKE = "rgba(96, 240, 248, 0.08)";
const AXIS_STROKE = "rgba(96, 240, 248, 0.15)";
const XAXIS_TICK = { fill: "var(--octopus-voltage)", fontSize: 11 };
const YAXIS_TICK = { fill: "var(--octopus-voltage)", fontSize: 11 };
const DOT_STYLE = { r: 3, strokeWidth: 1 };
const ACTIVE_DOT_STYLE = { r: 5, strokeWidth: 2 };
const LEGEND_STYLE = { paddingTop: "0.75rem", fontSize: "0.78rem" };

const CustomTooltip = ({ active, payload, label, metric }) => {
  if (!active || !payload?.length) return null;
  const filtered = payload.filter(p => p.value > 0);
  if (!filtered.length) return null;
  return (
    <div style={{
      background: "rgba(10, 18, 42, 0.95)", border: "1px solid rgba(96, 240, 248, 0.2)",
      borderRadius: "8px", padding: "0.75rem 1rem", backdropFilter: "blur(12px)",
    }}>
      <p style={{ color: "var(--octopus-ice)", fontWeight: "600", marginBottom: "0.5rem", fontSize: "0.8rem" }}>{label}</p>
      {filtered
        .sort((a, b) => b.value - a.value)
        .map((entry) => (
          <div key={entry.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: "1.5rem", marginBottom: "0.2rem" }}>
            <span style={{ color: entry.color, fontSize: "0.78rem", fontWeight: "500" }}>{entry.name}</span>
            <span style={{ color: "var(--octopus-ice)", fontSize: "0.78rem", fontWeight: "600" }}>
              {metric === "value" ? `£${entry.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : entry.value.toLocaleString()}
            </span>
          </div>
        ))}
    </div>
  );
};

const OverchargeTrendsChart = () => {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [metric, setMetric] = useState("value");
  const [hiddenSuppliers, setHiddenSuppliers] = useState(new Set());

  const loadTrends = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await apiGet("/dashboard/overcharge-trends", { timeout: 30000 });
      const t = data || { series: [], suppliers: [] };
      setTrends(t);
      // Default to just the Total line — 15 overlaid suppliers is unreadable.
      // Users click a supplier in the legend to break it back out.
      setHiddenSuppliers(new Set(t.suppliers || []));
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTrends(); }, [loadTrends]);

  const toggleSupplier = (supplier) => {
    setHiddenSuppliers(prev => {
      const next = new Set(prev);
      if (next.has(supplier)) next.delete(supplier);
      else next.add(supplier);
      return next;
    });
  };

  // Hooks MUST be called unconditionally on every render — keep these above
  // any early returns below to avoid React error #310 (hook count mismatch).
  const suppliers = useMemo(() => trends?.suppliers || [], [trends]);
  const suffix = useMemo(() => metric === "value" ? "_value" : "_count", [metric]);
  // Composite "Total" per month so the default view is one clean line.
  const series = useMemo(() => {
    const rows = trends?.series || [];
    const sup = trends?.suppliers || [];
    return rows.map((r) => {
      let tv = 0, tc = 0;
      sup.forEach((s) => { tv += r[`${s}_value`] || 0; tc += r[`${s}_count`] || 0; });
      return { ...r, Total_value: Math.round(tv * 100) / 100, Total_count: tc };
    });
  }, [trends]);

  if (loading) {
    return (
      <div className="card" style={{ marginBottom: "2rem", padding: "1.5rem" }} data-testid="trends-skeleton">
        <div style={{
          width: "40%", height: "1.25rem", borderRadius: "6px", marginBottom: "1rem",
          background: "linear-gradient(90deg, rgba(96,240,248,0.06) 25%, rgba(96,240,248,0.12) 50%, rgba(96,240,248,0.06) 75%)",
          backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite"
        }} />
        <div style={{
          width: "100%", height: "300px", borderRadius: "8px",
          background: "linear-gradient(90deg, rgba(96,240,248,0.04) 25%, rgba(96,240,248,0.08) 50%, rgba(96,240,248,0.04) 75%)",
          backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite"
        }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ marginBottom: "2rem", padding: "1.5rem", textAlign: "center" }} data-testid="trends-error">
        <p style={{ color: "var(--octopus-pink)", marginBottom: "0.5rem" }}>Failed to load overcharge trends</p>
        <button onClick={loadTrends} style={{
          border: "none", background: "transparent", color: "var(--octopus-pink)",
          cursor: "pointer", fontSize: "0.85rem", fontWeight: "600"
        }}>Retry</button>
      </div>
    );
  }

  if (!trends || !trends.series || trends.series.length === 0) {
    return (
      <div className="card" style={{ marginBottom: "2rem", padding: 0, overflow: "hidden" }} data-testid="overcharge-trends-chart">
        <div style={{
          padding: "1.25rem 1.5rem", display: "flex", justifyContent: "space-between",
          alignItems: "center", borderBottom: "1px solid rgba(96, 240, 248, 0.1)"
        }}>
          <div>
            <h2 style={{ fontSize: "1rem", fontWeight: "700", color: "var(--octopus-ice)", lineHeight: 1.2, marginBottom: "0.2rem" }}>
              Overcharges Over Time by Supplier
            </h2>
            <p style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)", opacity: 0.7 }}>
              Monthly breakdown of overcharge trends
            </p>
          </div>
        </div>
        <div style={{
          padding: "3rem 1.5rem", textAlign: "center"
        }}>
          <p style={{ color: "var(--octopus-voltage)", opacity: 0.5, fontSize: "0.9rem" }}>
            No overcharge data available yet. Upload invoices and price files to see trends.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: "2rem", padding: 0, overflow: "hidden" }} data-testid="overcharge-trends-chart">
      {/* Header */}
      <div style={{
        padding: "1.25rem 1.5rem 0", display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", flexWrap: "wrap", gap: "0.75rem"
      }}>
        <div>
          <h2 style={{ fontSize: "1rem", fontWeight: "700", color: "var(--octopus-ice)", lineHeight: 1.2, marginBottom: "0.2rem" }}>
            Overcharges Over Time by Supplier
          </h2>
          <p style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)", opacity: 0.7 }}>
            Total {metric === "value" ? "overcharge value" : "overcharge count"} — click a supplier in the legend to break it out
          </p>
        </div>

        {/* Metric Toggle */}
        <div data-testid="trends-metric-toggle" style={{
          display: "inline-flex", borderRadius: "8px", overflow: "hidden",
          border: "1px solid rgba(96, 240, 248, 0.2)", flexShrink: 0,
        }}>
          <button
            data-testid="trends-metric-value"
            onClick={() => setMetric("value")}
            style={{
              padding: "0.4rem 1rem", border: "none", cursor: "pointer",
              fontSize: "0.8rem", fontWeight: "600", transition: "all 0.15s ease",
              background: metric === "value" ? "rgba(96, 240, 248, 0.15)" : "transparent",
              color: metric === "value" ? "var(--octopus-ice)" : "var(--octopus-voltage)",
              borderRight: "1px solid rgba(96, 240, 248, 0.2)"
            }}
          >
            Value of overcharges
          </button>
          <button
            data-testid="trends-metric-count"
            onClick={() => setMetric("count")}
            style={{
              padding: "0.4rem 1rem", border: "none", cursor: "pointer",
              fontSize: "0.8rem", fontWeight: "600", transition: "all 0.15s ease",
              background: metric === "count" ? "rgba(240, 80, 248, 0.15)" : "transparent",
              color: metric === "count" ? "#F050F8" : "var(--octopus-voltage)",
            }}
          >
            Number of overcharges
          </button>
        </div>
      </div>

      {/* Chart */}
      <div style={{ padding: "1rem 1rem 0.5rem", width: "100%", height: 340 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
            <XAxis
              dataKey="month"
              tick={XAXIS_TICK}
              axisLine={{ stroke: AXIS_STROKE }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={YAXIS_TICK}
              axisLine={{ stroke: AXIS_STROKE }}
              tickLine={false}
              tickFormatter={(v) => metric === "value" ? (v >= 1000 ? `£${(v / 1000).toFixed(0)}k` : `£${v}`) : v.toLocaleString()}
              label={{
                value: metric === "value" ? "Overcharge Value (£)" : "Number of Overcharges",
                angle: -90, position: "insideLeft", offset: 0,
                style: YAXIS_TICK
              }}
            />
            <Tooltip content={<CustomTooltip metric={metric} />} />
            <Legend
              verticalAlign="bottom"
              wrapperStyle={LEGEND_STYLE}
              onClick={(e) => toggleSupplier(e.value)}
              formatter={(value) => (
                <span style={{
                  color: hiddenSuppliers.has(value) ? "rgba(96, 240, 248, 0.25)" : "var(--octopus-ice)",
                  textDecoration: hiddenSuppliers.has(value) ? "line-through" : "none",
                  cursor: "pointer", fontSize: "0.78rem",
                }}>{value}</span>
              )}
            />
            <Line
              type="monotone"
              dataKey={`Total${suffix}`}
              name="Total"
              stroke="#ffffff"
              strokeWidth={3}
              dot={false}
              activeDot={ACTIVE_DOT_STYLE}
              hide={hiddenSuppliers.has("Total")}
              connectNulls
            />
            {suppliers.map((supplier, idx) => (
              <Line
                key={supplier}
                type="monotone"
                dataKey={`${supplier}${suffix}`}
                name={supplier}
                stroke={SUPPLIER_COLORS[idx % SUPPLIER_COLORS.length]}
                strokeWidth={2}
                dot={DOT_STYLE}
                activeDot={ACTIVE_DOT_STYLE}
                hide={hiddenSuppliers.has(supplier)}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default OverchargeTrendsChart;
